import type { AxiosInstance } from "axios";
import { isValidId } from "../u2000Core/tools/U2000IdUtils";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3148";

// Definition of the Logical Unit Endpoints
type LogicalUnitEndpointsType = {
  INITLU: string;
  GET: (id: string | number) => string;
  UPDATE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;

  GETAFFILIATION: (id: string | number, affiliationId: string | number) => string;
  IMPORTAFFILIATION: (id: string | number) => string;
  CREATEAFFILIATION: (id: string | number) => string;
  UPDATEAFFILIATION: (id: string | number, affiliationId: string | number) => string;
  UPDATEAFFILIATIONPWD: (id: string | number, affiliationId: string | number) => string;
  DELETEAFFILIATION: (id: string | number, affiliationId: string | number) => string;
  AFFILIATIONAUDITS: (id: string | number, affiliationId: string | number) => string;
  AFFILIATIONLOADROLE: (id: string | number) => string;
  AFFILIATIONGETSECRETKEY: (id: string | number, affiliationId: string | number) => string;
  AFFILIATIONFINDUSERS: () => string;
  FINDCOUNTRIES: string;
  FINDSTATES: string;

  GETROLE: (id: string | number, roleId: string | number) => string;
  CREATEROLE: (id: string | number) => string;
  UPDATEROLE: (id: string | number, roleId: string | number) => string;
  DELETEROLE: (id: string | number, roleId: string | number) => string;
  ROLEAUDITS: (id: string | number, roleId: string | number) => string;
  LOADLOGICALUNIT: () => string;
  UPLOAD_ICON: (id: string | number) => string;
  DELETE_ICON: (id: string | number) => string;
};

/** Taille max icône organisation (5 Mo). */
export const MAX_ORGANIZATION_ICON_SIZE_BYTES = 5 * 1024 * 1024;

/** Accept HTML file input — extensions et types MIME autorisés (BL-0049). */
export const ORGANIZATION_ICON_ACCEPT =
  ".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";

const ORGANIZATION_ICON_ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".svg"];

const ORGANIZATION_ICON_ALLOWED_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];

/** Validation client du fichier icône (extension ou type MIME). */
export function isOrganizationIconFileAllowed(file: File): boolean {
  const name = file.name.toLowerCase();
  const hasAllowedExtension = ORGANIZATION_ICON_ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  const mime = (file.type || "").toLowerCase();
  const hasAllowedMime = mime.length > 0 && ORGANIZATION_ICON_ALLOWED_MIME_TYPES.includes(mime);
  return hasAllowedExtension || hasAllowedMime;
}

// Definition of the Logical Unit Endpoints
const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,

  GETAFFILIATION: (id, affiliationId) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}`,
  IMPORTAFFILIATION: (id) => `${LOGICALUNIT_URL}/${id}/affiliation/import`,
  CREATEAFFILIATION: (id) => `${LOGICALUNIT_URL}/${id}/affiliation/create`,
  UPDATEAFFILIATION: (id, affiliationId) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}/update`,
  UPDATEAFFILIATIONPWD: (id, affiliationId) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}/updatepwd`,
  DELETEAFFILIATION: (id, affiliationId) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}/delete`,
  AFFILIATIONAUDITS: (id, affiliationId) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}/audits`,
  AFFILIATIONLOADROLE: (id) => `${LOGICALUNIT_URL}/${id}/loadroles`,
  AFFILIATIONGETSECRETKEY: (id: string | number, affiliationId: string | number) => `${LOGICALUNIT_URL}/${id}/affiliation/${affiliationId}/secretkey`,
  AFFILIATIONFINDUSERS: () => `${LOGICALUNIT_URL}/findUsers`,
  FINDCOUNTRIES: `${LOGICALUNIT_URL}/findCountries`,
  FINDSTATES: `${LOGICALUNIT_URL}/findStates`,

  GETROLE: (id, roleId) => `${LOGICALUNIT_URL}/${id}/role/${roleId}`,
  CREATEROLE: (id) => `${LOGICALUNIT_URL}/${id}/role/create`,
  UPDATEROLE: (id, roleId) => `${LOGICALUNIT_URL}/${id}/role/${roleId}/update`,
  DELETEROLE: (id, roleId) => `${LOGICALUNIT_URL}/${id}/role/${roleId}/delete`,
  ROLEAUDITS: (id, roleId) => `${LOGICALUNIT_URL}/${id}/role/${roleId}/audits`,
  LOADLOGICALUNIT: () => `${LOGICALUNIT_URL}/loadLogicalUnitActions`,
  UPLOAD_ICON: (id) => `${LOGICALUNIT_URL}/${id}/icon/upload`,
  DELETE_ICON: (id) => `${LOGICALUNIT_URL}/${id}/icon/delete`,
};

// Codes des types de personnalisation (string, snake_case) – contrat API U3148
export const SysOrgPersonalizationNameType = {
  ADM_REGISTREFONCIERURL: "adm_registre_foncier_url",
  ORG_REGISTREFONCIERUSERNAME: "org_registre_foncier_username",
  ORG_REGISTREFONCIERPASSWORD: "org_registre_foncier_password",
  ADM_AWSACCESSKEYID: "adm_aws_access_key_id",
  ADM_AWSSECRETACCESSKEY: "adm_aws_secret_access_key",
  ADM_AWSREGION: "adm_aws_region",
  ADM_AWSBUCKETRFLOT: "adm_aws_bucket_rf_lot",
  ADM_AWSBUCKETREPORT: "adm_aws_bucket_report",
  ADM_AWSBUCKETASSETS: "adm_aws_bucket_assets",
  ADM_AWSBUCKETNAMERFLOTDETAIL: "adm_aws_bucket_name_rf_lot_detail",
  ADM_AWSBUCKETRFLOTDETAILPLAN: "adm_aws_bucket_rf_lot_detail_plan",
  ADM_AWSLAMBDAPDFTOJPEG: "adm_aws_lambda_pdf_to_jpeg",
  ADM_AWSLAMBDAPDFTOJPEGDPI: "adm_aws_lambda_pdf_to_jpeg_dpi",
  ADM_AWSLAMBDATEXTRACTTEXT: "adm_aws_lambda_extract_text",
  ADM_INCLUDE_SUBPROJECT: "adm_include_sub_projet",
  ADM_VACATION_NEGATIVE_THRESHOLD_HOURS: "adm_vacation_negative_threshold_hours",
  ORG_HOURLYMARKUP: "org_hourly_markup",
  /** Taux de vente horaire par défaut projet/sous-projet (U3121 initLU). */
  ORG_DEFAULT_RATE: "org_default_rate",
  /** Jours sans TEC facturable avant archivage projet (U3146 initLU). */
  ORG_PROJECT_INACTIVITY_DAYS: "org_project_inactivity_days",
  /** Fuseau IANA de référence pour le jour civil métier (BL-0105 / BL-0081). */
  ORG_TIMEZONE: "org_timezone",
  /** Pas d'arrondi des sessions de temps Mon temps (U3147) — 1, 5, 15, 30, 45 ou 60 minutes (BL-0106). */
  ORG_TIME_SESSION_ROUNDING_MINUTES: "org_time_session_rounding_minutes",
  ORG_IMP_PR_PM: "org_imp_pr_pm",
  ORG_IMP_PR_DF_TASK: "org_imp_pr_df_task",
  ORG_IMP_PR_SRVR: "org_imp_pr_srvr",
  ORG_GOOGLE_CALENDAR_CLIENT_ID: "org_google_calendar_client_id",
  ORG_GOOGLE_CALENDAR_CLIENT_SECRET: "org_google_calendar_client_secret",
  ORG_GOOGLE_CALENDAR_OAUTH_REDIRECT_URI: "org_google_calendar_oauth_redirect_uri",
  ORG_GOOGLE_CALENDAR_OAUTH_FRONTEND_REDIRECT_URL: "org_google_calendar_oauth_frontend_redirect_url",
  ORG_GOOGLE_CALENDAR_CALENDAR_ID: "org_google_calendar_calendar_id",
  ORG_GOOGLE_CALENDAR_CALENDAR_ID_DELIVERABLE: "org_google_calendar_calendar_id_deliverable",
  ORG_GOOGLE_CALENDAR_EVENT_TITLE_TEMPLATE: "org_google_calendar_event_title_template",
  /** Employeur D (Desjardins) : numéro de compagnie sans lettres (U3130 export). */
  ORG_EMPLOYEUR_D_COMPANY_NUMBER: "org_employeur_d_company_number",
  /** Employeur D : JSON mapping codes de gains en heures. */
  ORG_EMPLOYEUR_D_GAIN_MAPPING: "org_employeur_d_gain_mapping",
  /** Employeur D : seuil hebdomadaire surtemps pour rapport paie (heures). */
  ORG_EMPLOYEUR_D_WEEKLY_THRESHOLD: "org_employeur_d_weekly_threshold",
} as const;

/** Prefix des persos Google Calendar — gérées uniquement dans l'onglet dédié (BL-0068). */
export const U3148_GOOGLE_CALENDAR_PERSO_PREFIX = "org_google_calendar_";

export function isU3148GoogleCalendarPersonalization(code: string): boolean {
  return code.startsWith(U3148_GOOGLE_CALENDAR_PERSO_PREFIX);
}

/** Valeurs autorisées pour org_time_session_rounding_minutes (aligné backend U3147_ALLOWED_ROUNDING_MINUTES). */
export const U3148_TIME_SESSION_ROUNDING_OPTIONS = [1, 5, 15, 30, 45, 60] as const;

export function u3148IsValidTimeSessionRoundingMinutes(value: string): boolean {
  const n = Number(value.trim());
  return Number.isInteger(n) && (U3148_TIME_SESSION_ROUNDING_OPTIONS as readonly number[]).includes(n);
}

/** Persos sensibles ou gérées par OAuth — jamais envoyées depuis l'UI. */
const U3148_GOOGLE_CALENDAR_UI_EXCLUDED_FROM_UPDATE: readonly string[] = [
  "org_google_calendar_refresh_token",
];

export type SysOrgPersonalizationNameType = (typeof SysOrgPersonalizationNameType)[keyof typeof SysOrgPersonalizationNameType];

export interface U3148Country {
  id: string;
  countryCode?: string;
  countryName?: string;
  countryNameFr?: string;
  countryNameEn?: string;
  name?: string;
}

export interface U3148State {
  id: string;
  stateCode?: string;
  stateName?: string;
  stateNameFr?: string;
  stateNameEn?: string;
  name?: string;
}

/** Personnalisation en lecture : id, personalizationName (code string), personalizationValue, scope (lecture seule), version */
export interface U3148OrganizationPersonalization {
  id: string;
  personalizationName: string;
  personalizationValue: string;
  scope?: "admin" | "user";
  version: number;
}

// Organization
export interface U3148Organization {
  id: string;
  uuid: string;
  organizationName: string;
  /** Organisation par défaut (identifiant utilisé pour l'organisation par défaut) */
  organizationDefault: boolean;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressPostalCode?: string | null;
  country?: U3148Country | null;
  state?: U3148State | null;
  organizationIconKey?: string | null;
  organizationIconName?: string | null;
  organizationIconMimeType?: string | null;
  organizationIconSize?: number | null;
  organizationIconUrl?: string | null;
  version?: number;
  roles: U3148Role[];
  users: U3148Affiliation[];
  personalizations?: U3148OrganizationPersonalization[];
}

// Affiliations
export interface U3148AffiliationLogicalUnit {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  actions: string[];
}

export interface U3148RoleOrganization {
  id: string;
  organizationName: string;
}

// Roles
export interface U3148Role {
  id: string;
  idChecked: boolean;
  roleCode: string;
  roleDescription: string;
  actions: U3148RoleLogicalUnit[];
  organization: U3148RoleOrganization;
}

// Logical Units
export interface U3148RoleLogicalUnit {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  actions: string[];
}

// load pour le logical unit actions
export interface U3148LogicalUnit extends Record<string, unknown> {
  id: string;
  logicalUnitCode: string;
  logicalUnitDescription: string;
  logicalUnitActions: U3148LogicalUnitAction[];
}

export interface U3148LogicalUnitAction {
  id: string;
  idChecked: boolean;
  logicalUnitActionCode: string;
  logicalUnitActionDescription: string;
}

export interface U3148AffiliationRole {
  id: string;
  idChecked: boolean;
  roleCode: string;
  roleDescription: string;
}

export interface U3148AffiliationOrganization {
  id: string;
  organizationName: string;
}

export interface U3148Affiliation {
  id: string;
  idChecked: boolean;
  organization: U3148AffiliationOrganization;
  user: U3148User;
  roles: U3148AffiliationRole[];
  actions: U3148AffiliationLogicalUnit[];
}

export interface U3148User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  email: string;
  active: boolean;
  changePasswordOnLogin: boolean;
  secretKey: string;
  /** BL-0525 — connexion Google exclusive (flag global usager). */
  ssoEnabled: boolean;
  /** BL-0525 — instant ISO de liaison Google (lecture seule). */
  googleLinkedAt?: string | null;
  /** BL-0525 — effacer google_sub à la désactivation SSO. */
  unlinkGoogleIdentity?: boolean;
}

// Empty Objects
export const emptyOrganizationPersonalization: U3148OrganizationPersonalization = {
  id: "",
  personalizationName: "",
  personalizationValue: "",
  version: 0,
};

/** Payload update : id + version pour ligne existante ; ou seulement name + value pour nouvelle ligne */
export interface U3148OrganizationPersonalizationUpdatePayload {
  id?: string;
  personalizationName: string;
  personalizationValue: string;
  version?: number;
}

function shouldOmitPersonalizationOnUpdate(p: U3148OrganizationPersonalization): boolean {
  if (U3148_GOOGLE_CALENDAR_UI_EXCLUDED_FROM_UPDATE.includes(p.personalizationName)) {
    return true;
  }
  if (
    p.personalizationName === SysOrgPersonalizationNameType.ORG_GOOGLE_CALENDAR_CLIENT_SECRET &&
    !p.personalizationValue?.trim()
  ) {
    return true;
  }
  return false;
}

function preparePersonalizationsForUpdate(personalizations?: U3148OrganizationPersonalization[]): U3148OrganizationPersonalizationUpdatePayload[] {
  if (!Array.isArray(personalizations)) return [];
  return personalizations
    .filter((p) => !shouldOmitPersonalizationOnUpdate(p))
    .map((p) => {
      const isExistingRow = p.id != null && String(p.id).length > 0 && p.version != null && p.version > 0;
      if (isExistingRow) {
        return { id: p.id, personalizationName: p.personalizationName, personalizationValue: p.personalizationValue, version: p.version };
      }
      return { personalizationName: p.personalizationName, personalizationValue: p.personalizationValue };
    });
}

function pickCountryDisplayName(country: U3148Country, language: string): string {
  if (language === "fr" && country.countryNameFr) return country.countryNameFr;
  if (language === "en" && country.countryNameEn) return country.countryNameEn;
  return country.countryName ?? country.name ?? "";
}

function pickStateDisplayName(state: U3148State, language: string): string {
  if (language === "fr" && state.stateNameFr) return state.stateNameFr;
  if (language === "en" && state.stateNameEn) return state.stateNameEn;
  return state.stateName ?? state.name ?? "";
}

/** Enrichit country/state pour l’autocomplétion après GET ou upload icône. */
export function normalizeU3148OrganizationFromApi(org: U3148Organization, language: string): U3148Organization {
  const country = org.country;
  const state = org.state;
  return {
    ...org,
    country: country
      ? {
          ...country,
          name: pickCountryDisplayName(country, language),
          countryName: pickCountryDisplayName(country, language),
        }
      : null,
    state: state
      ? {
          ...state,
          name: pickStateDisplayName(state, language),
          stateName: pickStateDisplayName(state, language),
        }
      : null,
  };
}

function prepareCountryStateForApi(
  country?: U3148Country | null,
  state?: U3148State | null
): { country: { id: string } | null; state: { id: string } | null } {
  return {
    country: country && isValidId(country.id) ? { id: country.id } : null,
    state: state && isValidId(state.id) ? { id: state.id } : null,
  };
}

export type U3148OrganizationSaveMode = 'create' | 'update';

export type U3148OrganizationUpdateOptions = {
  /** BL-0660 — efface le client secret Google Calendar en BD (action explicite). */
  resetOrgGoogleCalendarClientSecret?: boolean;
};

export function buildOrganizationSavePayload(
  data: U3148Organization,
  personalizations: unknown,
  mode: U3148OrganizationSaveMode = 'create',
  updateOptions?: U3148OrganizationUpdateOptions,
) {
  const { country, state, personalizations: _p, users, roles, ...rest } = data;
  const geo = prepareCountryStateForApi(country, state);
  const base = {
    ...rest,
    country: geo.country,
    state: geo.state,
    personalizations,
    ...(mode === 'update' &&
      updateOptions?.resetOrgGoogleCalendarClientSecret === true && {
        resetOrgGoogleCalendarClientSecret: true,
      }),
  };
  if (mode === 'create') {
    return { ...base, users, roles };
  }
  return base;
}

/** BL-0525 — usager affiliation pour create/update : jamais googleLinkedAt ; password selon SSO. */
export function prepareAffiliationUserForApi(
  user: U3148User,
  options: { mode: "create" | "update"; previousSsoEnabled?: boolean },
): Record<string, unknown> {
  const ssoEnabled = user.ssoEnabled === true;
  const disablingSso = options.mode === "update" && options.previousSsoEnabled === true && !ssoEnabled;

  if (options.mode === "create") {
    const { googleLinkedAt: _googleLinkedAt, unlinkGoogleIdentity: _unlinkGoogleIdentity, password, ...userRest } = user;
    const prepared: Record<string, unknown> = {
      ...userRest,
      ssoEnabled,
    };
    if (!ssoEnabled) {
      prepared.password = password;
    }
    return prepared;
  }

  const {
    password,
    secretKey: _secretKey,
    id: _userId,
    googleLinkedAt: _googleLinkedAt,
    unlinkGoogleIdentity: _unlinkGoogleIdentity,
    ...userForApi
  } = user;

  const prepared: Record<string, unknown> = {
    ...userForApi,
    ssoEnabled,
  };

  if (ssoEnabled) {
    prepared.unlinkGoogleIdentity = false;
  } else if (disablingSso) {
    prepared.password = password;
    prepared.unlinkGoogleIdentity = true;
  }

  return prepared;
}

export function buildAffiliationCreatePayload(data: U3148Affiliation): Record<string, unknown> {
  return {
    ...data,
    user: prepareAffiliationUserForApi(data.user, { mode: "create" }),
  };
}

export function buildAffiliationUpdatePayload(
  data: U3148Affiliation,
  previousSsoEnabled?: boolean,
): Record<string, unknown> {
  const { idChecked: _idChecked, organization: _organization, roles, actions, user, id } = data;
  const version = (data as U3148Affiliation & { version?: number }).version;
  const userForApi = prepareAffiliationUserForApi(user, { mode: "update", previousSsoEnabled });

  const payload: Record<string, unknown> = {
    id,
    user: userForApi,
  };

  if (version !== undefined) {
    payload.version = version;
  }

  if (Array.isArray(roles) && roles.length > 0) {
    payload.roles = roles.map(({ idChecked: _roleChecked, ...role }) => role);
  }

  if (Array.isArray(actions) && actions.length > 0) {
    payload.actions = actions;
  }

  return payload;
}

export const emptyOrganization: U3148Organization = {
  id: "",
  uuid: "",
  organizationName: "",
  organizationDefault: false,
  addressLine1: null,
  addressLine2: null,
  addressCity: null,
  addressPostalCode: null,
  country: null,
  state: null,
  organizationIconKey: null,
  organizationIconName: null,
  organizationIconMimeType: null,
  organizationIconSize: null,
  organizationIconUrl: null,
  roles: [],
  users: [],
  personalizations: [],
};

export const emptyRole: U3148Role = {
  id: "",
  idChecked: false,
  roleCode: "",
  roleDescription: "",
  actions: [],
  organization: {
    id: "",
    organizationName: "",
  },
};

export const emptyUserDetails: U3148User = {
  id: "",
  username: "",
  firstName: "",
  lastName: "",
  password: "",
  email: "",
  active: true,
  changePasswordOnLogin: false,
  secretKey: "",
  ssoEnabled: false,
  googleLinkedAt: null,
  unlinkGoogleIdentity: false,
};

export const emptyAffiliation: U3148Affiliation = {
  id: "",
  idChecked: false,
  user: emptyUserDetails,
  roles: [],
  actions: [],
  organization: {
    id: "",
    organizationName: "",
  },
};

export const emptyLogicalUnit: U3148LogicalUnit = {
  id: "",
  logicalUnitCode: "",
  logicalUnitDescription: "",
  logicalUnitActions: [],
};

// API Calls - Initialization
export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

// API Calls - Get Organization by ID
export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

// API Calls - Update Organization (personalizations sans scope ; id+version pour existantes, name+value pour nouvelles)
export const doUpdateData = (
  axios: AxiosInstance,
  id: string | number,
  data: U3148Organization,
  updateOptions?: U3148OrganizationUpdateOptions,
) => {
  const payload = buildOrganizationSavePayload(
    data,
    preparePersonalizationsForUpdate(data.personalizations),
    'update',
    updateOptions,
  );
  return axios.post(LogicalUnitEndpoints.UPDATE(id), payload);
};

export const doFindCountries = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDCOUNTRIES, criterias);
};

export const doFindStates = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDSTATES, criterias);
};

// API Call - Audits Organization
export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {};
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

// APi Calls - Get Affiliation by ID
export const doGetaffiliationIdData = (axios: AxiosInstance, orgId: string | number, affiliationId: string | number, forceRefresh?: boolean) => {
  const params = forceRefresh ? { b: U2000GenerateUrlUUID() } : {};
  return axios.get(LogicalUnitEndpoints.GETAFFILIATION(orgId, affiliationId), { params });
};

// API Calls - Import User Affiliation
export const doImportAffiliationData = (axios: AxiosInstance, orgId: string | number, data: U3148Affiliation) => {
  return axios.post(LogicalUnitEndpoints.IMPORTAFFILIATION(orgId), data);
};

// API Calls - Create User Affiliation
export const doCreateAffiliationData = (axios: AxiosInstance, orgId: string | number, data: U3148Affiliation) => {
  return axios.post(LogicalUnitEndpoints.CREATEAFFILIATION(orgId), buildAffiliationCreatePayload(data));
};

// API Calls - Update User Affiliation
export const doUpdateAffiliationData = (
  axios: AxiosInstance,
  orgId: string | number,
  affiliationId: string | number,
  data: U3148Affiliation,
  previousSsoEnabled?: boolean,
) => {
  const payload = buildAffiliationUpdatePayload(data, previousSsoEnabled);
  return axios.post(LogicalUnitEndpoints.UPDATEAFFILIATION(orgId, affiliationId), payload);
};

// API Calls - Update User Affiliation Password
export const doUpdateAffiliationPwdData = (axios: AxiosInstance, orgId: string | number, affiliationId: string | number, data: U3148Affiliation) => {
  return axios.post(LogicalUnitEndpoints.UPDATEAFFILIATIONPWD(orgId, affiliationId), data);
};

// API Calls - Delete User Affiliation
export const doDeleteAffiliationData = (axios: AxiosInstance, orgId: string | number, affiliationId: string | number, data: U3148Affiliation) => {
  return axios.post(LogicalUnitEndpoints.DELETEAFFILIATION(orgId, affiliationId), data);
};

// API Calls - Audits User Affiliation
export const doGetAffiliationAuditsData = (axios: AxiosInstance, orgId: string | number, affiliationId: string | number) => {
  const params = {};
  return axios.get(LogicalUnitEndpoints.AFFILIATIONAUDITS(orgId, affiliationId), { params });
};

// Load Roles for Affiliation
export const doLoadRolesAffiliation = (axios: AxiosInstance, orgId: string | number) => {
  return axios.get(LogicalUnitEndpoints.AFFILIATIONLOADROLE(orgId));
};

// Get Secret Key for Affiliation
export const doGetSecretKeyAffiliationData = (axios: AxiosInstance, orgId: string | number, affiliationId: string | number) => {
  const params = {};
  return axios.get(LogicalUnitEndpoints.AFFILIATIONGETSECRETKEY(orgId, affiliationId), { params });
};

// Find Users for Affiliation
export const doFindUsersAffiliation = (axios: AxiosInstance, criterias: any) => {
  return axios.post(LogicalUnitEndpoints.AFFILIATIONFINDUSERS(), criterias);
};

// API Calls - Get Role by ID
export const doGetRoleIdData = (axios: AxiosInstance, orgId: string | number, roleId: string | number) => {
  const params = {};
  return axios.get(LogicalUnitEndpoints.GETROLE(orgId, roleId), { params });
};

// API Calls - Audits Role
export const doGetRoleAuditsData = (axios: AxiosInstance, orgId: string | number, roleId: string | number) => {
  const params = {};
  return axios.get(LogicalUnitEndpoints.ROLEAUDITS(orgId, roleId), { params });
};

// API Calls - Create Role
export const doCreateRoleData = (axios: AxiosInstance, orgId: string | number, data: U3148Role) => {
  return axios.post(LogicalUnitEndpoints.CREATEROLE(orgId), data);
};

// API Calls - Update Role
export const doUpdateRoleData = (axios: AxiosInstance, orgId: string | number, roleId: string | number, data: U3148Role) => {
  return axios.post(LogicalUnitEndpoints.UPDATEROLE(orgId, roleId), data);
};

// API Calls - Delete Role
export const doDeleteRoleData = (axios: AxiosInstance, orgId: string | number, roleId: string | number, data: U3148Role) => {
  return axios.post(LogicalUnitEndpoints.DELETEROLE(orgId, roleId), data);
};

// API Calls - Load Logical Unit Actions
export const doLoadLogicalUnitActions = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.LOADLOGICALUNIT());
};

/** POST upload icône organisation (multipart, champ file). */
export const doUploadOrganizationIcon = (axios: AxiosInstance, id: string | number, formData: FormData) => {
  return axios.post(LogicalUnitEndpoints.UPLOAD_ICON(id), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** POST suppression icône organisation. */
export const doDeleteOrganizationIcon = (axios: AxiosInstance, id: string | number) => {
  return axios.post(LogicalUnitEndpoints.DELETE_ICON(id));
};

// Handle API Errors for U3148 Logical Unit
// Note backend : BU314801/BU314802 sont réutilisés (AffiliationAlreadyExists / PasswordValidationError) —
// discrimination via errorType quand présent.
export function luHandleAppiError(t: TranslateFn, NS: string, err: any, errorResult: U2000ApiErrorResult) {
  if (err.errorCode === "BU314801") {
    if (err.errorType === "AffiliationAlreadyExists") {
      errorResult.genericErrors.push({
        title: t("errorTitle"),
        text: t("errorBU314801AffiliationExists", { ns: NS }),
      });
    } else {
      // OrganizationNotCurrent
      errorResult.genericErrors.push({
        title: t("errorTitle"),
        text: t("errorBU314801", { ns: NS }),
      });
    }
  } else if (err.errorCode === "BU314802") {
    if (err.errorType === "PasswordValidationError") {
      errorResult.fieldErrors["user.password"] = t("errorBU314802Password", { ns: NS });
    } else {
      // PersonalizationNotOrgScoped
      errorResult.genericErrors.push({
        title: t("errorTitle"),
        text: t("errorBU314802", { ns: NS }),
      });
    }
  } else if (err.errorCode === "BU314803") {
    errorResult.fieldErrors["user.password"] = t("errorBU314803", { ns: NS });
  } else if (err.errorCode === "BU210304") {
    errorResult.fieldErrors["user.password"] = t("errorBU210304", { ns: NS });
  } else if (err.errorCode === "BU210301") {
    errorResult.fieldErrors["user.password"] = t("errorBU210301", { ns: NS });
  } else if (err.errorCode === "BU210302") {
    errorResult.fieldErrors["user.password"] = t("errorBU210302", { ns: NS });
  } else if (err.errorCode === "BU210303") {
    errorResult.fieldErrors["user.email"] = t("errorBU210303", { ns: NS });
  } else if (err.errorCode === "BU314807") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314807", { ns: NS }),
    });
  } else if (err.errorCode === "BU314804") {
    // PersonalizationNotUserScoped
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314804", { ns: NS }),
    });
  } else if (err.errorCode === "BU314850") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314850", { ns: NS }),
    });
  } else if (err.errorCode === "BU314851") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314851", { ns: NS }),
    });
  } else if (err.errorCode === "BU314852") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314852", { ns: NS }),
    });
  } else if (err.errorCode === "BU314853") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314853", { ns: NS }),
    });
  } else if (err.errorCode === "BU314854") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314854", { ns: NS }),
    });
  } else if (err.errorCode === "BU314855") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314855", { ns: NS }),
    });
  } else if (err.errorCode === "BU314856") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314856", { ns: NS }),
    });
  } else if (err.errorCode === "BU314857") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU314857", { ns: NS }),
    });
  } else if (err.errorCode === "BU320101") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU320101", { ns: NS }),
    });
  } else if (err.errorCode === "BU320102") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU320102", { ns: NS }),
    });
  } else if (err.errorCode === "BU320103") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorBU320103", { ns: NS }),
    });
  }
}
